import { ArrowLeftOutlined } from '@ant-design/icons';
import { Alert, Button, Spin, Typography } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { type WorkflowTemplate, workflowsApi } from '@/api/workflows';
import { FormShell } from '@/form-renderer/FormShell';

const { Text } = Typography;

/**
 * Advisor-mode Smart Form runtime. Loads the template + mounts FormShell
 * with `mode='advisor'` so the shell honors the real advisor experience
 * (Next-gates on required content, no rail click-through, live values).
 *
 * TWO KEY DESIGN CHOICES for INFLOWS-3 AC05/AC06 (multi-tab isolation):
 *
 *   1. State lives entirely inside the FormShell component tree — no
 *      shared globals, no cross-tab localStorage — so distinct tabs
 *      with distinct URLs never collide. React remount-per-URL is the
 *      isolation primitive.
 *   2. The `applicationId` is threaded via URL param and passed into
 *      FormShell as a `key`, so navigating between two applications in
 *      the same tab forces a full remount (values + uploads + nav state
 *      all reset). Without the key, react-router would keep the same
 *      component tree alive across URL changes and blend state.
 *
 * TODO(BMO-ADVISOR-INTEGRATION): SWAP POINT — the template load is
 * currently `workflowsApi.get(templateId)`, same call the originator /
 * approver preview paths use. When real BMO integration lands:
 *   • The advisor session (via GAMX / BFF) should carry the
 *     workflow-selection context; today we trust the URL because it's
 *     minted by our own /advisor/dashboard.
 *   • Autosave / real submit / draft resume hit a per-application row
 *     on the BE (`GET/PUT /api/v1/applications/:id`), replacing FormShell's
 *     current in-memory-only values behavior.
 *
 * TODO(BMO-ADVISOR-INTEGRATION): `mode` ("r" read-only / "rw" read-write,
 * bmo-workflow-platform-spec.md:35) arrives as a query param when navigation
 * came from AdvisorEntryPage's resolver, but FormShell only has
 * `mode: 'preview' | 'advisor'` today — no read-only rendering exists.
 * `mode="advisor"` below is hardcoded regardless of the URL's `mode` value.
 * Real support needs a read-only variant added to FormShell (fields
 * rendered inert, Save/Next/Submit hidden or disabled) before this can be
 * wired up. See docs/pretend-advisor.md Swap-Point Index row #9.
 */
export function AdvisorApplicationPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { templateId, applicationId } = useParams<{
    templateId: string;
    applicationId: string;
  }>();
  // Advisor's locale drives every downstream string — the workflow header,
  // FormShell's initial language, all field labels. A hardcoded 'en' here
  // would leave a French-locale advisor reading English content until the
  // workflow's Welcome step language picker fires (and workflows with no
  // Welcome step never recover).
  const isFr = i18n.language.startsWith('fr');
  const language: 'en' | 'fr' = isFr ? 'fr' : 'en';
  const [template, setTemplate] = useState<WorkflowTemplate | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (!templateId) return;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    (async () => {
      try {
        const fresh = await workflowsApi.get(templateId);
        if (cancelled) return;
        setTemplate(fresh);
      } catch (err) {
        if (cancelled) return;
        setLoadError(err instanceof Error ? err.message : String(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [templateId]);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <Spin />
      </div>
    );
  }
  if (loadError || !template) {
    return (
      <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 16 }}>
        <Alert type="error" message={loadError ?? t('advisor.application.notFound')} />
        <div>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/advisor/dashboard')}>
            {t('advisor.application.backToDashboard')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      {/* Header strip identifying the application. Kept lightweight — the
          Smart Form's Welcome step is the real hero. Application id shown
          in a muted footer under the workflow name so the advisor (and QA)
          can eyeball which of their 5 open tabs is which. */}
      <div style={{ padding: '16px 0 8px', display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Text strong style={{ fontSize: 18, color: '#0f172a' }}>
          {(isFr ? template.nameFr : template.nameEn) ||
            template.nameEn ||
            template.appName ||
            t('advisor.application.untitled')}
        </Text>
        <Text type="secondary" style={{ fontSize: 12, letterSpacing: 0.2 }}>
          {/* Punctuation lives inside the i18n string so FR can render the
              non-breaking space before the colon (French typographical
              convention) — a hardcoded `: ` in JSX would render en-style
              in both locales. */}
          {t('advisor.application.idLine', { id: applicationId })}
        </Text>
      </div>
      {/* key={applicationId} — see the class-level comment. Two applications
          in the same tab force a full FormShell remount so state resets. */}
      <FormShell
        key={applicationId}
        template={template}
        language={language}
        mode="advisor"
        onSaveAndExit={() => navigate('/advisor/dashboard')}
      />
    </div>
  );
}
